// src/hooks/useHelpShortcut.js
// A mount-once, always-active `?` keyboard shortcut that opens the help
// control center (FIND-02).
//
// D-12: this is the minimum guard that makes a bare `?` safe, chosen after
// the cost was stated in discussion -- with no guard, a question mark typed
// into the email composer or a transaction note opens the drawer and
// swallows the character, and agents write question marks constantly. The
// alternative `Cmd/Ctrl+/` was considered and rejected: FIND-02 names `?`
// explicitly, and `?` is the convention readers arrive with (GitHub, Gmail,
// Slack, Linear, Notion).
//
// Deliberately NOT checked: no dialog state, no in-progress tour, and no
// focus-location condition beyond the text-entry guard below. One check,
// one reason.

import React from 'react';

/**
 * True when `el` is a text-entry surface: an input, a textarea, a select, or
 * any element with active content-editable state. The content-editable check
 * reads the live DOM property rather than the attribute, so an element
 * marked contenteditable="false" correctly reads as NOT text entry -- the
 * negative-of-the-negative case an attribute-presence check would get wrong.
 */
export function isTextEntryTarget(el) {
    if (!el) return false;
    const tag = el?.tagName?.toLowerCase?.();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    return el.isContentEditable === true;
}

/**
 * Registers a single, mount-once `document` keydown listener that calls
 * `onTrigger` when `?` is pressed outside of text entry, with no modifier
 * held. `onTrigger` is held in a ref refreshed on every render, so the
 * listener itself attaches exactly once per mount regardless of how often
 * the caller re-renders -- the same shape useDialogShell.js uses for its
 * own mount-once Escape listener.
 */
export default function useHelpShortcut(onTrigger) {
    const onTriggerRef = React.useRef(onTrigger);
    React.useEffect(() => { onTriggerRef.current = onTrigger; });

    React.useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key !== '?') return;
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            if (isTextEntryTarget(event.target) || isTextEntryTarget(document.activeElement)) return;
            event.preventDefault();
            onTriggerRef.current();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
